import React, { useEffect, useState } from 'react';
import { Star, Pin, Eye, UserCheck, DollarSign, TrendingUp, Clock, Activity, Zap, ChevronRight, ArrowUp, ArrowDown, Play, Pause, Trash2, Calendar, CreditCard, Info, CheckCircle2, LayoutList, ExternalLink, Database, Server, History, Receipt, AlertCircle, RefreshCw, Mail, Settings, X, BarChart3, MousePointer, Sparkles, SplitSquareHorizontal, Edit, Target, Timer, ArrowRight, AlertTriangle, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface FeaturedAstrologersManagerProps {
  astrologers: any[];
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
  setShowCreatePin: (type: 'astrologer' | null) => void;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(amount);
};
// Mock Audit Logs
const initialLogs = [{
  id: 'LOG-1',
  time: '10:42:15',
  type: 'auto_swap',
  message: 'Авто-замена: FA-01 завершен. Активирован FA-03.',
  status: 'success'
}, {
  id: 'LOG-2',
  time: '10:41:02',
  type: 'payment',
  message: 'Оплата подтверждена для FA-02. Статус изменен на "Активен".',
  status: 'success'
}, {
  id: 'LOG-3',
  time: '10:30:00',
  type: 'notification',
  message: 'Отправлено 24 SMS-уведомлений о старте кампаний.',
  status: 'info'
}];
export function FeaturedAstrologersManager({
  astrologers: initialAstrologers,
  setDetailPanel,
  success,
  setShowCreatePin
}: FeaturedAstrologersManagerProps) {
  const [campaigns, setCampaigns] = useState(initialAstrologers);
  const [activeModal, setActiveModal] = useState<'logs' | 'payment' | 'timing' | 'analytics' | 'ab_test' | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [logs, setLogs] = useState(initialLogs);
  // Sync with props
  useEffect(() => {
    setCampaigns(initialAstrologers);
  }, [initialAstrologers]);
  const addLog = (type: string, message: string, status: string) => {
    setLogs(prev => [{
      id: `LOG-${Date.now()}`,
      time: new Date().toLocaleTimeString('ru-RU'),
      type,
      message,
      status
    }, ...prev].slice(0, 50));
  };
  const handleAction = (action: string, campaign: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    switch (action) {
      case 'view_profile':
        window.open(`/astrologer/${campaign.id}`, '_blank');
        break;
      case 'view_admin':
        setDetailPanel({
          type: 'featured-astrologer',
          title: campaign.astrologer,
          data: campaign
        });
        break;
      case 'view_advertiser':
        setDetailPanel({
          type: 'user-profile',
          title: campaign.advertiser,
          data: {
            name: campaign.advertiser,
            type: campaign.advertiserType
          }
        });
        break;
      case 'view_slot':
        setDetailPanel({
          type: 'slot-detail',
          title: campaign.slot,
          data: {
            name: campaign.slot
          }
        });
        break;
      case 'analytics':
        setSelectedCampaign(campaign);
        setActiveModal('analytics');
        break;
      case 'payment':
        setSelectedCampaign(campaign);
        setActiveModal('payment');
        break;
      case 'timing':
        setSelectedCampaign(campaign);
        setActiveModal('timing');
        break;
      case 'ab_test':
        setSelectedCampaign(campaign);
        setActiveModal('ab_test');
        break;
      case 'activate':
        if (campaign.paymentStatus !== 'paid') {
          addLog('error', `Отказ активации ${campaign.id}: Требуется оплата`, 'error');
          alert('Невозможно активировать: требуется оплата');
          return;
        }
        success(`Кампания ${campaign.id} активирована`);
        addLog('activation', `Кампания ${campaign.id} успешно активирована`, 'success');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'active'
        } : c));
        break;
      case 'pause':
        success(`Кампания ${campaign.id} приостановлена`);
        addLog('pause', `Кампания ${campaign.id} поставлена на паузу`, 'warning');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'paused'
        } : c));
        break;
      case 'fastTrack':
        success(`Fast Track включен для ${campaign.id}`);
        addLog('fast_track', `Fast Track включен для ${campaign.id}`, 'info');
        break;
      case 'delete':
        if (confirm(`Удалить кампанию ${campaign.id}?`)) {
          success(`Кампания ${campaign.id} удалена`);
          setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
        }
        break;
    }
  };
  // --- METRICS CALCULATION ---
  const totalImpressions = campaigns.reduce((acc, c) => acc + (c.impressions || 0), 0);
  const totalClicks = campaigns.reduce((acc, c) => acc + (c.clicks || 0), 0);
  const totalBookings = campaigns.reduce((acc, c) => acc + (c.bookings || 0), 0);
  const totalRevenue = campaigns.reduce((acc, c) => acc + (c.status === 'active' ? c.price : 0), 0);
  const avgConsultationPrice = 5000; // Mock avg check for consultation
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100).toFixed(2) : '0.00';
  const avgCr = totalClicks > 0 ? (totalBookings / totalClicks * 100).toFixed(2) : '0.00';
  const avgCpa = totalBookings > 0 ? Math.round(totalRevenue / totalBookings) : 0;
  const avgRpm = totalImpressions > 0 ? Math.round(totalRevenue / totalImpressions * 1000) : 0;
  const renderKpis = () => <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-id="element-10983">
      <Card padding="sm" hoverable className="bg-indigo-50 border-indigo-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-10984">
        <div className="text-[10px] text-indigo-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-10985">
          <Pin className="w-3 h-3" data-id="element-10986" /> Активные
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-10987">
          {campaigns.filter(c => c.status === 'active').length}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-blue-50 border-blue-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-10988">
        <div className="text-[10px] text-blue-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-10989">
          <Eye className="w-3 h-3" data-id="element-10990" /> Показы
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-10991">
          {totalImpressions.toLocaleString()}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-sky-50 border-sky-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-10992">
        <div className="text-[10px] text-sky-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-10993">
          <MousePointer className="w-3 h-3" data-id="element-10994" /> Клики
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-10995">
          {totalClicks.toLocaleString()}
        </div>
        <div className="text-[10px] text-sky-600 mt-1" data-id="element-10996">CTR {avgCtr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-emerald-50 border-emerald-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-10997">
        <div className="text-[10px] text-emerald-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-10998">
          <UserCheck className="w-3 h-3" data-id="element-10999" /> Записи
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11000">
          {totalBookings.toLocaleString()}
        </div>
        <div className="text-[10px] text-emerald-600 mt-1" data-id="element-11001">CR {avgCr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-green-50 border-green-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11002">
        <div className="text-[10px] text-green-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11003">
          <DollarSign className="w-3 h-3" data-id="element-11004" /> Доход (Ads)
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11005">
          {formatCurrency(totalRevenue)}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-amber-50 border-amber-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11006">
        <div className="text-[10px] text-amber-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11007">
          <Target className="w-3 h-3" data-id="element-11008" /> CPA
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11009">
          {formatCurrency(avgCpa)}
        </div>
        <div className="text-[10px] text-amber-600 mt-1" data-id="element-11010">Цена за запись</div>
      </Card>
      <Card padding="sm" hoverable className="bg-purple-50 border-purple-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11011">
        <div className="text-[10px] text-purple-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11012">
          <Activity className="w-3 h-3" data-id="element-11013" /> RPM
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11014">
          {formatCurrency(avgRpm)}
        </div>
        <div className="text-[10px] text-purple-600 mt-1" data-id="element-11015">
          Доход на 1000 пок.
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-slate-50 border-slate-200 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11016">
        <div className="text-[10px] text-slate-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11017">
          <CreditCard className="w-3 h-3" data-id="element-11018" /> Ср. чек конс.
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11019">
          {formatCurrency(avgConsultationPrice)}
        </div>
      </Card>
    </div>;
  const renderAiRecommendations = () => <Card padding="sm" className="mb-6 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50" data-id="element-11020">
      <div className="flex items-start gap-4" data-id="element-11021">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0" data-id="element-11022">
          <Sparkles className="w-5 h-5 text-indigo-600" data-id="element-11023" />
        </div>
        <div className="flex-1" data-id="element-11024">
          <h3 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-11025">
            AI Рекомендации для платформы
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11026">
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11027">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11028">
                Оптимизация профиля
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11029">
                Астролог FA-02 имеет низкий CR (3.4%). Рекомендуется обновить
                главное фото и добавить видео-визитку для повышения доверия.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11030">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11031">
                Повышение позиции
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11032">
                Слот "Каталог астрологов - TOP 3" свободен со следующей недели.
                Предложите текущим рекламодателям апгрейд со скидкой 10%.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11033">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11034">
                A/B тест заголовков
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11035">
                Запустите автоматическое A/B тестирование заголовков профилей в
                каталоге для увеличения общего CTR на 15-20%.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>;
  const renderTable = () => <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-11036">
      <div className="p-4 border-b border-slate-200 bg-white flex justify-between items-center" data-id="element-11037">
        <div className="flex items-center gap-3" data-id="element-11038">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-11039">
            <Star className="w-4 h-4 text-amber-500" data-id="element-11040" />
          </div>
          <div data-id="element-11041">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-11042">
              Управление продвижением астрологов
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-11043">
              Ads Manager для профилей специалистов
            </p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-11044">
          <Button variant="outline" size="sm" icon={<History className="w-4 h-4" data-id="element-11046" />} onClick={() => setActiveModal('logs')} data-id="element-11045">
            Audit Logs
          </Button>
          <Button variant="primary" size="sm" icon={<Pin className="w-4 h-4" data-id="element-11048" />} onClick={() => setShowCreatePin('astrologer')} data-id="element-11047">
            Создать кампанию
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-11049">
        <table className="w-full text-left whitespace-nowrap" data-id="element-11050">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-11051">
            <tr data-id="element-11052">
              <th className="px-4 py-3 font-medium" data-id="element-11053">Кампания / Астролог</th>
              <th className="px-4 py-3 font-medium" data-id="element-11054">Рекламодатель</th>
              <th className="px-4 py-3 font-medium" data-id="element-11055">Слот / Позиция</th>
              <th className="px-4 py-3 font-medium" data-id="element-11056">Таймер / Период</th>
              <th className="px-4 py-3 font-medium" data-id="element-11057">Коммерция</th>
              <th className="px-4 py-3 font-medium" data-id="element-11058">
                Воронка (Показы → Клики → Записи)
              </th>
              <th className="px-4 py-3 font-medium" data-id="element-11059">Статус / Очередь</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-11060">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-11061">
            {campaigns.map(c => {
            const ctr = c.impressions > 0 ? (c.clicks / c.impressions * 100).toFixed(1) : '0.0';
            const cr = c.clicks > 0 ? (c.bookings / c.clicks * 100).toFixed(1) : '0.0';
            const isPaid = c.paymentStatus === 'paid';
            // Timer logic simulation
            let timerColor = 'text-slate-500';
            let timerText = 'Ожидание';
            if (c.status === 'active') {
              timerColor = 'text-emerald-600';
              timerText = 'Осталось 14 дн.';
            } else if (c.status === 'queued') {
              timerColor = 'text-amber-600';
              timerText = 'Старт через 4 дн.';
            }
            return <tr key={c.id} className="hover:bg-slate-50 transition-colors group bg-white" data-id="element-11062">
                  {/* Astrologer Info */}
                  <td className="px-4 py-3" data-id="element-11063">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11064">
                      <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:underline" onClick={() => handleAction('view_admin', c)} data-id="element-11065">
                        {c.id}
                      </span>
                      {c.creatives?.length > 1 && <Badge variant="pro" size="sm" className="text-[9px] py-0 cursor-pointer" onClick={() => handleAction('ab_test', c)} data-id="element-11066">
                          <SplitSquareHorizontal className="w-2.5 h-2.5 mr-0.5" data-id="element-11067" />{' '}
                          A/B Test
                        </Badge>}
                    </div>
                    <div className="text-sm font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1" onClick={() => handleAction('view_profile', c)} data-id="element-11068">
                      {c.astrologer}{' '}
                      <ExternalLink className="w-3 h-3 opacity-50" data-id="element-11069" />
                    </div>
                  </td>

                  {/* Advertiser */}
                  <td className="px-4 py-3" data-id="element-11070">
                    <div className="text-xs font-medium text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5" onClick={() => handleAction('view_advertiser', c)} data-id="element-11071">
                      <UserCheck className="w-3.5 h-3.5 text-slate-400" data-id="element-11072" />
                      {c.advertiser}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-11073">
                      {c.advertiserType === 'astrologer' ? 'Сам астролог' : 'Продюсер / Агентство'}
                    </div>
                  </td>

                  {/* Slot & Position */}
                  <td className="px-4 py-3" data-id="element-11074">
                    <div className="text-xs font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5 mb-1" onClick={() => handleAction('view_slot', c)} data-id="element-11075">
                      <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-11076" />
                      {c.slot}
                    </div>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-11077">
                      {c.position}
                    </Badge>
                  </td>

                  {/* Timer & Period */}
                  <td className="px-4 py-3" data-id="element-11078">
                    <div className={`text-xs font-bold flex items-center gap-1.5 mb-1 cursor-pointer hover:underline ${timerColor}`} onClick={() => handleAction('timing', c)} data-id="element-11079">
                      <Clock className="w-3.5 h-3.5" data-id="element-11080" />
                      {timerText}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1" data-id="element-11081">
                      <Calendar className="w-3 h-3" data-id="element-11082" />
                      {c.periodStart} — {c.periodEnd}
                    </div>
                  </td>

                  {/* Commerce */}
                  <td className="px-4 py-3" data-id="element-11083">
                    <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-11084">
                      {formatCurrency(c.price)}
                    </div>
                    <div className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded cursor-pointer transition-colors ${isPaid ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`} onClick={() => handleAction('payment', c)} data-id="element-11085">
                      <Receipt className="w-2.5 h-2.5" data-id="element-11086" />
                      {isPaid ? 'Оплачено' : 'Ожидает оплаты'}
                    </div>
                  </td>

                  {/* Funnel Metrics */}
                  <td className="px-4 py-3" data-id="element-11087">
                    <div className="flex items-center gap-4 cursor-pointer hover:bg-slate-50 p-1 rounded transition-colors" onClick={() => handleAction('analytics', c)} data-id="element-11088">
                      <div className="flex flex-col items-center w-16" data-id="element-11089">
                        <span className="text-xs font-bold text-slate-900" data-id="element-11090">
                          {c.impressions.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11091">
                          Показы
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-11092" />
                      <div className="flex flex-col items-center w-16" data-id="element-11093">
                        <span className="text-xs font-bold text-blue-600" data-id="element-11094">
                          {c.clicks.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11095">
                          CTR {ctr}%
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-11096" />
                      <div className="flex flex-col items-center w-16" data-id="element-11097">
                        <span className="text-xs font-bold text-emerald-600" data-id="element-11098">
                          {c.bookings.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11099">
                          CR {cr}%
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Status & Queue */}
                  <td className="px-4 py-3" data-id="element-11100">
                    <div className="flex flex-col gap-1.5 items-start" data-id="element-11101">
                      <Badge variant={c.status === 'active' ? 'success' : c.status === 'queued' ? 'warning' : 'neutral'} size="sm" data-id="element-11102">
                        {c.status === 'active' ? 'Транслируется' : c.status === 'queued' ? 'В очереди' : 'Пауза'}
                      </Badge>

                      {c.queuePosition && <div className="text-[10px] text-indigo-600 font-medium bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-indigo-100 flex items-center gap-1" onClick={() => handleAction('timing', c)} data-id="element-11103">
                          <LayoutList className="w-3 h-3" data-id="element-11104" />
                          Очередь: #{c.queuePosition}
                        </div>}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right" data-id="element-11105">
                    <div className="flex items-center justify-end gap-1" data-id="element-11106">
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Аналитика" onClick={() => handleAction('analytics', c)} data-id="element-11107">
                        <BarChart3 className="w-3.5 h-3.5" data-id="element-11108" />
                      </Button>
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Редактировать" onClick={() => handleAction('view_admin', c)} data-id="element-11109">
                        <Edit className="w-3.5 h-3.5" data-id="element-11110" />
                      </Button>
                      {c.status === 'active' ? <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-amber-500 hover:bg-amber-50" title="Пауза" onClick={() => handleAction('pause', c)} data-id="element-11111">
                          <Pause className="w-3.5 h-3.5" data-id="element-11112" />
                        </Button> : <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-emerald-500 hover:bg-emerald-50" title="Активировать" onClick={e => handleAction('activate', c, e)} data-id="element-11113">
                          <Play className="w-3.5 h-3.5 fill-current" data-id="element-11114" />
                        </Button>}
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-rose-400 hover:text-rose-600 hover:bg-rose-50" title="Удалить" onClick={() => handleAction('delete', c)} data-id="element-11115">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-11116" />
                      </Button>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
  const renderModals = () => {
    if (!activeModal) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setActiveModal(null);
    }} data-id="element-11117">
        {/* LOGS MODAL */}
        {activeModal === 'logs' && <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11118">
            <div className="p-4 border-b border-slate-200 bg-slate-900 text-white flex justify-between items-center shrink-0" data-id="element-11119">
              <div className="flex items-center gap-3" data-id="element-11120">
                <Server className="w-5 h-5 text-indigo-400" data-id="element-11121" />
                <div data-id="element-11122">
                  <h3 className="font-bold" data-id="element-11123">Audit Logs: Ads Manager</h3>
                  <p className="text-[10px] text-slate-400" data-id="element-11124">
                    Журнал событий рекламной платформы
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" icon={<X className="w-5 h-5" data-id="element-11126" />} onClick={() => setActiveModal(null)} data-id="element-11125" />
            </div>
            <div className="p-0 overflow-y-auto bg-slate-950 flex-1 font-mono text-xs" data-id="element-11127">
              {logs.map(log => <div key={log.id} className="flex gap-4 p-3 border-b border-slate-800 hover:bg-slate-900/50" data-id="element-11128">
                  <div className="text-slate-500 shrink-0" data-id="element-11129">{log.time}</div>
                  <div className={`shrink-0 w-24 ${log.status === 'error' ? 'text-rose-400' : log.status === 'warning' ? 'text-amber-400' : log.status === 'success' ? 'text-emerald-400' : 'text-blue-400'}`} data-id="element-11130">
                    [{log.type.toUpperCase()}]
                  </div>
                  <div className="text-slate-300" data-id="element-11131">{log.message}</div>
                </div>)}
            </div>
          </Card>}

        {/* PAYMENT MODAL */}
        {activeModal === 'payment' && selectedCampaign && <Card className="w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11132">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11133">
              <div className="flex items-center gap-2" data-id="element-11134">
                <Receipt className="w-5 h-5 text-indigo-600" data-id="element-11135" />
                <h3 className="font-bold text-slate-900" data-id="element-11136">
                  Детали оплаты кампании
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11138" />} onClick={() => setActiveModal(null)} data-id="element-11137" />
            </div>
            <div className="p-6 space-y-6" data-id="element-11139">
              <div className="text-center" data-id="element-11140">
                <div className="text-sm text-slate-500 mb-1" data-id="element-11141">
                  Сумма к оплате
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-11142">
                  {formatCurrency(selectedCampaign.price || 0)}
                </div>
                <Badge variant={selectedCampaign.paymentStatus === 'paid' ? 'success' : 'warning'} className="mt-2" data-id="element-11143">
                  {selectedCampaign.paymentStatus === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm" data-id="element-11144">
                <div className="flex justify-between" data-id="element-11145">
                  <span className="text-slate-500" data-id="element-11146">ID Кампании</span>
                  <span className="font-bold text-slate-900" data-id="element-11147">
                    {selectedCampaign.id}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11148">
                  <span className="text-slate-500" data-id="element-11149">Инвойс</span>
                  <span className="font-mono text-indigo-600" data-id="element-11150">
                    {selectedCampaign.invoiceId || 'INV-PENDING'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11151">
                  <span className="text-slate-500" data-id="element-11152">Транзакция</span>
                  <span className="font-mono text-slate-900" data-id="element-11153">
                    {selectedCampaign.paymentId || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11154">
                  <span className="text-slate-500" data-id="element-11155">Рекламодатель</span>
                  <span className="font-medium text-slate-900" data-id="element-11156">
                    {selectedCampaign.advertiser}
                  </span>
                </div>
              </div>

              {selectedCampaign.paymentStatus !== 'paid' && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-id="element-11157">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" data-id="element-11158" />
                  <div className="text-sm text-amber-800" data-id="element-11159">
                    <strong data-id="element-11160">Блокировка активации:</strong> Кампания не будет
                    запущена до подтверждения оплаты.
                  </div>
                </div>}

              <div className="flex gap-3 pt-4 border-t border-slate-100" data-id="element-11161">
                <Button variant="outline" className="flex-1" onClick={() => setActiveModal(null)} data-id="element-11162">
                  Закрыть
                </Button>
                {selectedCampaign.paymentStatus !== 'paid' && <Button variant="primary" className="flex-1" onClick={() => {
              success('Оплата подтверждена');
              addLog('payment', `Ручное подтверждение оплаты для ${selectedCampaign.id}`, 'success');
              setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? {
                ...c,
                paymentStatus: 'paid',
                paymentId: 'PAY-MANUAL-' + Date.now()
              } : c));
              setActiveModal(null);
            }} data-id="element-11163">
                    Подтвердить оплату
                  </Button>}
              </div>
            </div>
          </Card>}

        {/* TIMING MODAL */}
        {activeModal === 'timing' && selectedCampaign && <Card className="w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11164">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11165">
              <div className="flex items-center gap-2" data-id="element-11166">
                <Clock className="w-5 h-5 text-indigo-600" data-id="element-11167" />
                <h3 className="font-bold text-slate-900" data-id="element-11168">Тайминг и Очередь</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11170" />} onClick={() => setActiveModal(null)} data-id="element-11169" />
            </div>
            <div className="p-6 space-y-4 text-sm" data-id="element-11171">
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11172">
                <span className="text-slate-500" data-id="element-11173">Статус</span>
                <Badge variant={selectedCampaign.status === 'active' ? 'success' : 'warning'} data-id="element-11174">
                  {selectedCampaign.status === 'active' ? 'Транслируется' : 'В очереди'}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11175">
                <span className="text-slate-500" data-id="element-11176">Период</span>
                <span className="font-medium text-slate-900" data-id="element-11177">
                  {selectedCampaign.periodStart} — {selectedCampaign.periodEnd}
                </span>
              </div>

              {selectedCampaign.status === 'queued' && <>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11178">
                    <span className="text-slate-500" data-id="element-11179">Позиция в очереди</span>
                    <span className="font-bold text-indigo-600" data-id="element-11180">
                      #{selectedCampaign.queuePosition}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11181">
                    <span className="text-slate-500" data-id="element-11182">Ожидаемый старт</span>
                    <span className="font-bold text-slate-900" data-id="element-11183">
                      {selectedCampaign.expectedStartAt || 'Неизвестно'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11184">
                    <span className="text-slate-500" data-id="element-11185">Время ожидания</span>
                    <span className="font-medium text-amber-600" data-id="element-11186">
                      {selectedCampaign.waitTime || '—'}
                    </span>
                  </div>
                </>}

              <div className="mt-6 bg-indigo-50 p-4 rounded-xl text-xs text-indigo-800 flex gap-2" data-id="element-11187">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-11188" />
                <p data-id="element-11189">
                  Система автоматически активирует кампанию при освобождении
                  слота "{selectedCampaign.slot}" (onExpire event).
                </p>
              </div>
            </div>
          </Card>}

        {/* ANALYTICS MODAL */}
        {activeModal === 'analytics' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11190">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11191">
              <div className="flex items-center gap-2" data-id="element-11192">
                <BarChart3 className="w-5 h-5 text-indigo-600" data-id="element-11193" />
                <h3 className="font-bold text-slate-900" data-id="element-11194">
                  Аналитика кампании: {selectedCampaign.astrologer}
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11196" />} onClick={() => setActiveModal(null)} data-id="element-11195" />
            </div>
            <div className="p-6" data-id="element-11197">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-11198">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11199">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11200">Показы</div>
                  <div className="text-2xl font-bold text-slate-900" data-id="element-11201">
                    {selectedCampaign.impressions.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11202">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11203">Клики</div>
                  <div className="text-2xl font-bold text-blue-600" data-id="element-11204">
                    {selectedCampaign.clicks.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11205">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11206">CTR</div>
                  <div className="text-2xl font-bold text-indigo-600" data-id="element-11207">
                    {selectedCampaign.impressions > 0 ? (selectedCampaign.clicks / selectedCampaign.impressions * 100).toFixed(2) : '0.00'}
                    %
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11208">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11209">Записи</div>
                  <div className="text-2xl font-bold text-emerald-600" data-id="element-11210">
                    {selectedCampaign.bookings.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="h-48 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center mb-6" data-id="element-11211">
                <div className="text-slate-400 flex items-center gap-2" data-id="element-11212">
                  <BarChart3 className="w-5 h-5" data-id="element-11213" />
                  <span data-id="element-11214">График конверсий (Mock)</span>
                </div>
              </div>

              <div className="flex justify-end" data-id="element-11215">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-11216">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}

        {/* A/B TEST MODAL */}
        {activeModal === 'ab_test' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11217">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11218">
              <div className="flex items-center gap-2" data-id="element-11219">
                <SplitSquareHorizontal className="w-5 h-5 text-indigo-600" data-id="element-11220" />
                <h3 className="font-bold text-slate-900" data-id="element-11221">
                  A/B Тестирование профиля
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11223" />} onClick={() => setActiveModal(null)} data-id="element-11222" />
            </div>
            <div className="p-6" data-id="element-11224">
              <p className="text-sm text-slate-600 mb-6" data-id="element-11225">
                Сравнение эффективности различных вариантов карточки для
                кампании <strong data-id="element-11226">{selectedCampaign.id}</strong>.
              </p>

              <div className="space-y-4" data-id="element-11227">
                {selectedCampaign.creatives?.map((cr: any, idx: number) => <div key={cr.id} className={`p-4 rounded-xl border ${idx === 0 ? 'border-emerald-400 bg-emerald-50/30' : 'border-slate-200 bg-white'} flex items-center justify-between`} data-id="element-11228">
                    <div className="flex items-center gap-4" data-id="element-11229">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${idx === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-11230">
                        {idx === 0 ? 'A' : 'B'}
                      </div>
                      <div data-id="element-11231">
                        <div className="font-bold text-slate-900" data-id="element-11232">
                          {cr.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-11233">
                          ID: {cr.id}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-6 text-center" data-id="element-11234">
                      <div data-id="element-11235">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-11236">
                          CTR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-11237">
                          {cr.ctr}
                        </div>
                      </div>
                      <div data-id="element-11238">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-11239">
                          CR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-11240">
                          {cr.cr}
                        </div>
                      </div>
                    </div>
                    {idx === 0 && <Badge variant="success" data-id="element-11241">Winner</Badge>}
                  </div>)}
              </div>

              <div className="mt-6 flex justify-end" data-id="element-11242">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-11243">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-11244">
      {renderKpis()}
      {renderAiRecommendations()}
      {renderTable()}
      {renderModals()}
    </div>;
}